import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";
import { Table } from "../../components/ui/Table";
import { Modal } from "../../components/ui/Modal";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

interface DocRow {
  id: number;
  nombre: string;
  tipo: string;
  clienteId?: number | null;
  esComun?: boolean;
  esVisible?: boolean;
  adjuntarCertificadoEmail?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

const TIPOS_OPTIONS = [
  { value: "poliza", label: "Póliza" },
  { value: "parte_accidentes", label: "Parte accidentes" },
  { value: "poliza_generica", label: "Póliza genérica" },
  { value: "manual", label: "Manual" },
  { value: "otro", label: "Otro" },
];

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

export function DocumentosPage() {
  const [list, setList] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState<{ id: number; razonSocial?: string }[]>([]);
  const [tab, setTab] = useState<"todos" | "comunes" | "cliente">("todos");
  const [clienteFilter, setClienteFilter] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editVisibleId, setEditVisibleId] = useState<number | null>(null);
  const [uploadForm, setUploadForm] = useState({
    nombre: "",
    tipo: "poliza",
    cliente_id: "",
    es_comun: "false",
    es_visible: "true",
    adjuntar_certificado_email: "false",
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [clienteSelectError, setClienteSelectError] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadError, setDownloadError] = useState("");

  const handleDownload = async (row: DocRow) => {
    setDownloadingId(row.id);
    setDownloadError("");
    try {
      const response = await api.get<Blob>(`/admin/documentos/${row.id}/download`, {
        responseType: "blob",
      });
      if (response.data.type.split(";")[0] !== "application/pdf") {
        throw new Error("La respuesta no es un PDF");
      }
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = /\.pdf$/i.test(row.nombre) ? row.nombre : `${row.nombre}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setDownloadError("No se pudo descargar el documento. Inténtalo de nuevo.");
    } finally {
      setDownloadingId(null);
    }
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (tab === "comunes") params.es_comun = "true";
      else if (tab === "cliente" && clienteFilter) params.cliente_id = clienteFilter;
      const res = await api.get<DocRow[]>("/admin/documentos", { params });
      setList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [tab, clienteFilter]);

  const fetchClientes = useCallback(async () => {
    try {
      const res = await api.get<{ data: { id: number; razonSocial?: string }[] }>("/admin/clientes", { params: { limit: 500, estado: "activo" } });
      setClientes(res.data?.data ?? []);
    } catch {
      setClientes([]);
    }
  }, []);

  useEffect(() => {
    fetchClientes();
  }, [fetchClientes]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError("");
    setClienteSelectError(false);
    if (!uploadForm.nombre.trim()) {
      setUploadError("El nombre es obligatorio");
      return;
    }
    if (uploadForm.es_comun === "false" && !uploadForm.cliente_id) {
      setUploadError("Selecciona un cliente para este documento");
      setClienteSelectError(true);
      return;
    }
    if (!uploadFile) {
      setUploadError("Selecciona un fichero PDF");
      return;
    }
    if (uploadFile.type !== "application/pdf") {
      setUploadError("Solo se permiten PDF");
      return;
    }
    if (uploadFile.size > MAX_FILE_SIZE) {
      setUploadError("El fichero no puede superar 20MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      fd.append("nombre", uploadForm.nombre.trim());
      fd.append("tipo", uploadForm.tipo);
      fd.append("es_comun", uploadForm.es_comun);
      fd.append("es_visible", uploadForm.es_visible);
      fd.append("adjuntar_certificado_email", uploadForm.adjuntar_certificado_email);
      if (uploadForm.es_comun === "false") fd.append("cliente_id", uploadForm.cliente_id);
      await api.post("/admin/documentos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadOpen(false);
      setUploadForm({ nombre: "", tipo: "poliza", cliente_id: "", es_comun: "false", es_visible: "true", adjuntar_certificado_email: "false" });
      setUploadFile(null);
      fetchList();
    } catch (err: unknown) {
      setUploadError((err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const handleToggleAdjuntoCertificado = async (id: number, current: boolean) => {
    try {
      await api.patch(`/admin/documentos/${id}`, { adjuntar_certificado_email: !current });
      fetchList();
    } catch {
      /* ignore */
    }
  };

  const handleToggleVisible = async (id: number, current: boolean) => {
    try {
      await api.patch(`/admin/documentos/${id}`, { es_visible: !current });
      setEditVisibleId(null);
      fetchList();
    } catch {
      setEditVisibleId(null);
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    try {
      await api.delete(`/admin/documentos/${deleteId}`);
      setDeleteId(null);
      fetchList();
    } catch {
      setDeleteId(null);
    }
  };

  const filteredList = list;

  const columns = [
    { key: "nombre", header: "Nombre", render: (r: DocRow) => r.nombre },
    { key: "tipo", header: "Tipo", render: (r: DocRow) => r.tipo },
    { key: "cliente", header: "Cliente", render: (r: DocRow) => (r.esComun ? "Común" : (r.clienteId ? `#${r.clienteId}` : "-")) },
    { key: "visible", header: "Visible", render: (r: DocRow) => (r.esVisible ? "Sí" : "No") },
    {
      key: "adjCert",
      header: "Adj. certificado",
      render: (r: DocRow) => (
        <input
          type="checkbox"
          className="h-4 w-4 accent-[#1A3AB8] cursor-pointer"
          checked={!!r.adjuntarCertificadoEmail}
          onChange={() => handleToggleAdjuntoCertificado(r.id, !!r.adjuntarCertificadoEmail)}
          title="Adjuntar automáticamente al email de certificado del piloto"
          aria-label="Adjuntar automáticamente al email de certificado del piloto"
        />
      ),
    },
    { key: "createdAt", header: "Fecha subida", render: (r: DocRow) => (r.createdAt ? new Date(r.createdAt).toLocaleDateString() : "-") },
    {
      key: "actions",
      header: "Acciones",
      render: (r: DocRow) => (
        <div className="flex gap-2">
          <button type="button" onClick={() => void handleDownload(r)} disabled={downloadingId !== null} className="text-[#1A3AB8] hover:underline text-sm disabled:opacity-50">
            {downloadingId === r.id ? "Descargando…" : "Descargar"}
          </button>
          <Button size="sm" variant="ghost" onClick={() => setEditVisibleId(editVisibleId === r.id ? null : r.id)}>Editar visibilidad</Button>
          {editVisibleId === r.id && (
            <Button size="sm" variant="secondary" onClick={() => handleToggleVisible(r.id, !!r.esVisible)}>{r.esVisible ? "Ocultar" : "Mostrar"}</Button>
          )}
          <Button size="sm" variant="danger" onClick={() => setDeleteId(r.id)}>Eliminar</Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#2C2C2C]">Documentos</h1>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex max-w-full gap-2 overflow-x-auto border-b border-gray-200">
          {(["todos", "comunes", "cliente"] as const).map((t) => (
            <button key={t} type="button" onClick={() => setTab(t)} className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium ${tab === t ? "border-b-2 border-[#1A3AB8] text-[#1A3AB8]" : "text-gray-600"}`}>
              {t === "todos" ? "Todos" : t === "comunes" ? "Documentos comunes" : "Por cliente"}
            </button>
          ))}
        </div>
        {tab === "cliente" && (
          <Select label="Cliente" options={[{ value: "", label: "Seleccionar" }, ...clientes.map((c) => ({ value: String(c.id), label: c.razonSocial ?? "" }))]} value={clienteFilter} onChange={(e) => setClienteFilter(e.target.value)} className="w-56" />
        )}
        <Button onClick={() => setUploadOpen(true)}>Subir documento</Button>
      </div>

      {downloadError && <p role="alert" className="text-sm text-red-600">{downloadError}</p>}
      <Table<DocRow> columns={columns} data={filteredList} loading={loading} emptyMessage="No hay documentos" />

      <Modal isOpen={uploadOpen} onClose={() => { setUploadOpen(false); setUploadError(""); setClienteSelectError(false); }} title="Subir documento" footer={null}>
        <form onSubmit={handleUpload} className="space-y-4">
          <Input label="Nombre" value={uploadForm.nombre} onChange={(e) => setUploadForm((f) => ({ ...f, nombre: e.target.value }))} required />
          <Select label="Tipo" options={TIPOS_OPTIONS} value={uploadForm.tipo} onChange={(e) => setUploadForm((f) => ({ ...f, tipo: e.target.value }))} />
          <Select label="Ámbito" options={[{ value: "false", label: "Por cliente" }, { value: "true", label: "Documento común" }]} value={uploadForm.es_comun} onChange={(e) => setUploadForm((f) => ({ ...f, es_comun: e.target.value }))} />
          {uploadForm.es_comun === "false" && (
            <Select
              label="Cliente *"
              options={[{ value: "", label: "Seleccionar" }, ...clientes.map((c) => ({ value: String(c.id), label: c.razonSocial ?? "" }))]}
              value={uploadForm.cliente_id}
              onChange={(e) => {
                setUploadForm((f) => ({ ...f, cliente_id: e.target.value }));
                if (e.target.value) setClienteSelectError(false);
              }}
              error={clienteSelectError ? "Selecciona un cliente" : undefined}
            />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Visible</label>
            <select value={uploadForm.es_visible} onChange={(e) => setUploadForm((f) => ({ ...f, es_visible: e.target.value }))} className="border rounded px-3 py-2 w-full">
              <option value="true">Sí</option>
              <option value="false">No</option>
            </select>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[#1A3AB8] shrink-0"
              checked={uploadForm.adjuntar_certificado_email === "true"}
              onChange={(e) => setUploadForm((f) => ({ ...f, adjuntar_certificado_email: e.target.checked ? "true" : "false" }))}
            />
            <span className="text-sm text-gray-700">Adjuntar automáticamente al email de certificado</span>
          </label>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fichero (PDF, máx 20MB)</label>
            <input type="file" accept=".pdf,application/pdf" onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)} className="border rounded px-3 py-2 w-full" />
          </div>
          {uploadError && <p className="text-red-600 text-sm">{uploadError}</p>}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="secondary" onClick={() => setUploadOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={uploading}>Subir</Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog isOpen={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="Eliminar documento" message="¿Eliminar este documento?" confirmLabel="Eliminar" variant="danger" />
    </div>
  );
}
